import React from 'react';
import { Zap, Globe, Tag, Clock, TrendingDown, PhoneCall } from 'lucide-react';

const FeaturesGrid = () => {
  const features = [
    {
      icon: Zap,
      title: 'Same-Day & Next-Day Delivery',
      description: 'Elevate every order with premium same-day and next-day delivery options.',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      icon: Tag,
      title: 'Automated Shipping Labels',
      description: 'Generate and print shipping labels automatically, saving time and reducing manual errors in your shipping process.',
      color: 'from-purple-500 to-pink-600'
    },
    {
      icon: Globe,
      title: 'Nation Coverage',
      description: 'Deliver anywhere across the country with our extensive courier network.',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: Clock,
      title: 'Seamless Integration',
      description: 'Our platform integrates effortlessly with eCommerce websites, offering a smooth workflow from order placement to delivery.',
      color: 'from-blue-800 to-emerald-600'
    },
    {
      icon: TrendingDown,
      title: 'RTO Prediction',
      description: 'Use predictive insights to flag high-risk shipments early and reduce return-to-origin losses.',
      color: 'from-rose-500 to-pink-600'
    },
    {
      icon: PhoneCall,
      title: 'Order & Address Confirmation',
      description: 'Confirm customer intent and validate address details before dispatch to improve delivery success rates.',
      color: 'from-indigo-500 to-blue-600'
    }
  ];

  return (
    <section className="relative py-24 bg-gradient-to-br from-gray-50 to-white overflow-hidden" id="features">
      {/* Decorative Elements with Animations */}
      <div className="absolute inset-0">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}></div>
        
        {/* Animated Floating orbs */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-gradient-to-br from-blue-200/20 to-blue-300/10 rounded-full blur-3xl animate-float-slow"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-gradient-to-br from-blue-100/20 to-blue-200/10 rounded-full blur-3xl animate-float-slower"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-blue-800 font-semibold text-sm uppercase tracking-wide">Comprehensive Features</span>
          <h2 className="text-4xl md:text-5xl font-bold mt-2 mb-4 text-gray-900">
            Courier Features for <span className="text-blue-800">eCommerce Growth</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            Advanced features designed to simplify order fulfillment and improve overall logistics efficiency
          </p>
        </div>
        
        {/* Features with Curved Connecting Lines */}
        <div className="relative">
          {/* Curved Connection Lines - SVG */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
            <defs>
              <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#1d4ed8" stopOpacity="0.3" />
              </linearGradient>
              
              {/* Animated gradient for flow effect */}
              <linearGradient id="flowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.1">
                  <animate attributeName="stop-opacity" values="0.1;0.5;0.1" dur="3s" repeatCount="indefinite" />
                </stop>
                <stop offset="50%" stopColor="#1d4ed8" stopOpacity="0.5">
                  <animate attributeName="stop-opacity" values="0.5;0.8;0.5" dur="3s" repeatCount="indefinite" />
                </stop>
                <stop offset="100%" stopColor="#1e40af" stopOpacity="0.1">
                  <animate attributeName="stop-opacity" values="0.1;0.5;0.1" dur="3s" repeatCount="indefinite" />
                </stop>
              </linearGradient>
            </defs>
            
            {/* Curved paths connecting features */}
            <path 
              d="M 25% 30%, Q 50% 20%, 75% 30%" 
              stroke="url(#lineGradient)" 
              strokeWidth="2" 
              fill="none"
              strokeDasharray="5,5"
            >
              <animate attributeName="stroke-dashoffset" from="0" to="-10" dur="1s" repeatCount="indefinite" />
            </path>
            
            <path 
              d="M 25% 70%, Q 50% 80%, 75% 70%" 
              stroke="url(#lineGradient)" 
              strokeWidth="2" 
              fill="none"
              strokeDasharray="5,5"
            >
              <animate attributeName="stroke-dashoffset" from="0" to="-10" dur="1s" repeatCount="indefinite" />
            </path>
            
            <path 
              d="M 50% 30%, Q 50% 50%, 50% 70%" 
              stroke="url(#flowGradient)" 
              strokeWidth="2" 
              fill="none"
              strokeDasharray="5,5"
            >
              <animate attributeName="stroke-dashoffset" from="0" to="-10" dur="1s" repeatCount="indefinite" />
            </path>
          </svg>
          
          {/* Feature Cards */}
          <div className="relative grid md:grid-cols-2 gap-8" style={{ zIndex: 1 }}>
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index} 
                  className="relative group"
                >
                  {/* Gradient border effect */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} rounded-3xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-300`}></div>
                  
                  <div className="relative bg-white p-10 rounded-3xl border-2 border-gray-200 group-hover:border-blue-300 shadow-lg group-hover:shadow-2xl transition-all duration-300 overflow-hidden">
                    <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-lg">{feature.description}</p>
                    
                    {/* Decorative corner */}
                    <div className="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-tl from-blue-50 to-transparent rounded-tl-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    
                    {/* Bottom accent line */}
                    <div className="absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r ${feature.color} group-hover:w-full transition-all duration-500"></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      <style jsx>{`
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-30px) translateX(20px); }
        }
        
        @keyframes float-slower {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(30px) translateX(-20px); }
        }
        
        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }
        
        .animate-float-slower {
          animation: float-slower 10s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default FeaturesGrid;